// src/components/SearchBar.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaSpinner } from 'react-icons/fa';
import { useTranslation as useI18n } from 'react-i18next';
import { useTranslation } from '../contexts/TranslationContext';
import {
  resolveSearchLangs,
  resolvePreferredLangs,
  searchAyahsQuick,
  LANG_LABELS,
} from '../utils/clientSearch';
import { highlightSnippet, buildSurahHighlightPath } from '../utils/searchHighlight';

const SearchBar = React.memo(function SearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const desktopInputRef = useRef(null);
  const navigate = useNavigate();
  const { i18n } = useI18n();
  const { selectedTranslations } = useTranslation();

  const langs = resolveSearchLangs(selectedTranslations, i18n.language);
  const preferredLangs = resolvePreferredLangs(selectedTranslations, i18n.language);

  const goToSearchPage = useCallback(
    (q) => {
      const trimmed = (q ?? query).trim();
      if (!trimmed) return;
      setIsSearchVisible(false);
      setIsOpen(false);
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    },
    [navigate, query]
  );

  const fetchResults = useCallback(
    async (q, signal) => {
      if (!q.trim()) return;
      setLoading(true);
      setError(null);
      try {
        const response = await searchAyahsQuick(q, { langs, preferredLangs, signal });
        if (signal?.aborted) return;
        setResults(Array.isArray(response) ? response : []);
        setIsOpen(true);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Search error:', err);
        setError('Error fetching results');
        setResults([]);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [langs, preferredLangs]
  );

  useEffect(() => {
    if (!isSearchVisible) return undefined;
    const controller = new AbortController();
    const delayDebounce = setTimeout(() => {
      if (query.trim().length > 1) {
        fetchResults(query, controller.signal);
      } else {
        setResults([]);
        setIsOpen(false);
        setLoading(false);
      }
    }, 280);
    return () => {
      clearTimeout(delayDebounce);
      controller.abort();
    };
  }, [query, fetchResults, isSearchVisible]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        event.target.getAttribute('data-search-toggle') !== 'true'
      ) {
        setIsSearchVisible(false);
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isSearchVisible) return undefined;
    const prev = document.body.style.overflow;
    if (window.matchMedia('(max-width: 639px)').matches) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isSearchVisible]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      goToSearchPage();
    } else if (e.key === 'Escape') {
      setIsSearchVisible(false);
      setIsOpen(false);
      setQuery('');
    }
  };

  const toggleSearch = () => {
    setIsSearchVisible((v) => {
      const next = !v;
      if (next) {
        setTimeout(() => {
          const isMobile = window.matchMedia('(max-width: 639px)').matches;
          (isMobile ? inputRef : desktopInputRef).current?.focus();
        }, 80);
      } else {
        setIsOpen(false);
      }
      return next;
    });
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    const isMobile = window.matchMedia('(max-width: 639px)').matches;
    (isMobile ? inputRef : desktopInputRef).current?.focus();
  };

  const closeSearch = () => {
    setIsSearchVisible(false);
    setIsOpen(false);
    setQuery('');
  };

  const showResultsPanel = loading || error || (isOpen && query.trim().length > 1);

  const resultsBody = (
    <>
      {loading && (
        <p className="text-sm text-gray-500 flex items-center px-4 py-3">
          <FaSpinner className="animate-spin mr-2" /> Searching…
        </p>
      )}
      {error && !loading && <p className="text-sm text-red-500 px-4 py-3">{error}</p>}
      {!loading && !error && results.length === 0 && (
        <p className="text-sm text-gray-500 px-4 py-3">No results found.</p>
      )}
      {!loading && results.length > 0 && (
        <>
          <p className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100 dark:border-gray-700">
            Top {results.length} matches
          </p>
          <ul className="overflow-y-auto flex-1">
            {results.map((item) => (
              <li key={item.ayah_id} className="border-b border-gray-50 dark:border-gray-700 last:border-0">
                <Link
                  to={buildSurahHighlightPath(item.surah_id, item.ayah_id, query)}
                  state={{ highlightQuery: query, fromSearch: true }}
                  onClick={closeSearch}
                  className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs text-gray-400 truncate">
                      {item.surah_name} · {item.ayah_id}
                    </p>
                    <span className="text-[10px] uppercase tracking-wide text-teal-600 bg-teal-50 dark:bg-teal-900/40 dark:text-teal-300 px-2 py-0.5 rounded-full flex-shrink-0">
                      {item.matchLabel}
                    </span>
                  </div>
                  {item.matchFields?.arabic && (
                    <p
                      className="font-arabic text-blue-600 dark:text-blue-300 text-xl leading-relaxed text-right mb-1"
                      dir="rtl"
                    >
                      {item.arabic_text}
                    </p>
                  )}
                  {item.translationSnippet && (
                    <p className="text-sm text-gray-700 dark:text-gray-200 line-clamp-2">
                      {item.translationLang && (
                        <span className="text-[10px] font-semibold text-teal-600 mr-1">
                          {LANG_LABELS[item.translationLang] || item.translationLang}
                        </span>
                      )}
                      {highlightSnippet(item.translationSnippet, query)}
                    </p>
                  )}
                  {item.preferredTranslationSnippet && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                      <span className="font-semibold mr-1">
                        {LANG_LABELS[item.preferredTranslationLang] || 'Preferred'}:
                      </span>
                      {highlightSnippet(item.preferredTranslationSnippet, query)}
                    </p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => goToSearchPage()}
            className="w-full text-center text-sm font-semibold text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 py-3 hover:bg-teal-100 dark:hover:bg-teal-900/50 transition"
          >
            View all results
          </button>
        </>
      )}
    </>
  );

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleSearch}
        className="text-white hover:text-teal-400 transition-colors duration-200 p-2"
        aria-label="Toggle search"
        data-search-toggle="true"
        aria-expanded={isSearchVisible}
      >
        <FaSearch className="w-6 h-6" />
      </button>

      {/* Desktop: input stays inside the app bar, aligned with the icon */}
      {isSearchVisible && (
        <div className="hidden sm:flex absolute right-full mr-1 top-1/2 -translate-y-1/2 items-center bg-white rounded-full shadow-lg overflow-hidden w-72 md:w-80">
          <FaSearch className="ml-3 text-gray-400 w-4 h-4 flex-shrink-0" />
          <input
            ref={desktopInputRef}
            type="text"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            className="w-full px-3 py-2 text-black text-sm focus:outline-none"
            placeholder="Search ayah, translation, tafsir…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyPress}
            aria-label="Search Quran"
          />
          {query ? (
            <button type="button" onClick={clearSearch} className="p-2 text-gray-500" aria-label="Clear">
              <FaTimes className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      )}

      {/* Mobile backdrop — below header (z-50) visually dimmed content only */}
      {isSearchVisible && (
        <div
          className="fixed inset-x-0 bottom-0 top-[60px] z-[45] bg-gray-900/45 sm:hidden"
          onClick={closeSearch}
        />
      )}

      {/*
        Results always open BELOW the app bar:
        - mobile: fixed under the 60px header
        - desktop: absolute under the search control (grows downward only)
      */}
      {isSearchVisible && (
        <div
          className="
            fixed z-[55] left-3 right-3 top-[60px] pt-2
            sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:pt-0
          "
        >
          {/* Mobile input under header */}
          <div className="flex sm:hidden items-center bg-white rounded-2xl shadow-lg overflow-hidden mb-2">
            <FaSearch className="ml-3 text-gray-400 w-4 h-4 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              className="w-full px-3 py-3 text-black text-base focus:outline-none"
              placeholder="Search ayah, translation, tafsir…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyPress}
              aria-label="Search Quran"
            />
            <button
              type="button"
              onClick={query ? clearSearch : closeSearch}
              className="p-2 text-gray-500"
              aria-label={query ? 'Clear' : 'Close'}
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {showResultsPanel && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-xl shadow-lg w-full max-h-[min(70vh,calc(100dvh-5.5rem))] sm:max-h-96 overflow-hidden flex flex-col border border-gray-100 dark:border-gray-700">
              {resultsBody}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default SearchBar;
